import { CAL_MAG_OPTIONS } from '@/lib/constants';
import { parseTypedAmount } from '@/lib/number';
import { paramByKey, refusedValueMessage } from '@/lib/waterModel';
import { evaluateTier, type Context, type ThresholdTable, type Tier } from '@/lib/thresholds';

type Num = number | string | null | undefined;

const toNum = (v: Num): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** "100/110"; a lone first number reads plain, a lone second number reads "—/110". Null when neither exists. */
export function formatPair(first: Num, second: Num): string | null {
  const a = toNum(first);
  const b = toNum(second);
  if (a === null && b === null) return null;
  if (b === null) return String(a);
  return `${a ?? '—'}/${b}`;
}

/** A Cal/Mag word from before the number pairs, as a label; anything else (a typed pair) as written. */
function legacyCalMagLabel(text: string | null | undefined): string | null {
  const t = text?.trim();
  if (!t) return null;
  return CAL_MAG_OPTIONS.find((o) => o.value === t.toLowerCase())?.label ?? t;
}

/** Calcium/magnesium numbers when either exists, else the legacy word or typed pair. */
export function formatCalMag(calcium: Num, magnesium: Num, legacy?: string | null): string | null {
  return formatPair(calcium, magnesium) ?? legacyCalMagLabel(legacy);
}

const TIER_ORDER: Tier[] = ['ok', 'warning', 'critical'];

/** The worse of the two alkalinity tiers: either number being low is a low alkalinity. Each number
 *  is judged by its own band in the farm's table (the second follows the first unless overridden). */
export function alkalinityTier(table: ThresholdTable | undefined, first: Num, second: Num, ctx?: Context): Tier {
  const tiers = [
    evaluateTier(table, 'alkalinity_ppm', toNum(first), ctx),
    evaluateTier(table, 'alkalinity_2', toNum(second), ctx),
  ];
  return tiers.sort((x, y) => TIER_ORDER.indexOf(y) - TIER_ORDER.indexOf(x))[0];
}

/** One side of a typed pair: a number, `'clear'` (type a dash), or `undefined` (left blank: keep what is there). */
export type PairSide = number | 'clear' | undefined;
export type PairInput = { first: PairSide; second: PairSide } | { error: string };

const CLEAR_MARK = /^[-\u2013\u2014]$/;
/** The example shown with each pair field. Alkalinity is a test-kit range; neither end is named low or high. */
export const PAIR_EXAMPLE: Record<string, string> = { alkalinity: '140/120', calcium: '300/900' };
export const PAIR_HINT: Record<string, string> = {
  alkalinity: `Test-kit range, e.g. ${PAIR_EXAMPLE.alkalinity}`,
  calcium: `Calcium/magnesium, e.g. ${PAIR_EXAMPLE.calcium}`,
};
const PAIR_ERROR = 'Type the two numbers like 140/120';

function parseSide(text: string): PairSide | { error: string } {
  const t = text.trim();
  if (t === '') return undefined;
  if (CLEAR_MARK.test(t)) return 'clear';
  const r = parseTypedAmount(t, { integer: true, example: '100' });
  if ('error' in r) return { error: r.error };
  return r.value === null ? undefined : r.value;
}

/**
 * What a pair cell's text means. "100/110" sets both numbers ("320/1,094": a thousands comma is fine);
 * "100" sets the first only; "/110" and "100/" set only the typed side and never blank the other.
 * A dash clears a side ("100/-", "-/110"); the whole cell empty clears both. Whole numbers only.
 */
export function parsePairInput(text: string): PairInput {
  const t = text.trim();
  if (t === '') return { first: 'clear', second: 'clear' };
  const parts = t.split('/');
  if (parts.length > 2) return { error: PAIR_ERROR };
  const sides = parts.map(parseSide);
  const bad = sides.find((x): x is { error: string } => typeof x === 'object' && x !== null && 'error' in x);
  if (bad) return bad;
  const [first, second] = sides as PairSide[];
  if (first === undefined && second === undefined) return { error: PAIR_ERROR };
  return { first, second };
}

/**
 * Column updates a parsed pair makes against what is saved: only sides that were typed and changed.
 * `resend` (the pair was refused last time) also sends the sides left alone: a refused pair saved nothing,
 * so the number the screen already shows is not on the server.
 */
export function pairChanges(
  input: { first: PairSide; second: PairSide },
  current: { first: Num; second: Num },
  resend = false
): { first?: number | null; second?: number | null } {
  const out: { first?: number | null; second?: number | null } = {};
  for (const side of ['first', 'second'] as const) {
    const typed = input[side];
    if (typed === undefined) {
      if (resend) out[side] = toNum(current[side]);
      continue;
    }
    const next = typed === 'clear' ? null : typed;
    if (resend || next !== toNum(current[side])) out[side] = next;
  }
  return out;
}

/**
 * The refusal sentence for one number of a pair: the pair's neutral label ("Alkalinity", "Cal/Mag"), never the
 * second half's own name, and that number's own band (a half with no band names no range).
 */
export function pairRefusedMessage(table: ThresholdTable | undefined, ownerKey: string, halfKey: string, value: string): string {
  const message = refusedValueMessage(table, halfKey, value);
  const half = paramByKey(halfKey);
  const owner = paramByKey(ownerKey);
  return half && owner && half.label !== owner.label ? message.replace(half.label, owner.label) : message;
}

/** The cell's colour: the worse of the pair's two numbers (each judged by its own band, a number with no band is 'ok'). */
export function worstTier(tiers: Tier[]): Tier {
  return [...tiers].sort((x, y) => TIER_ORDER.indexOf(y) - TIER_ORDER.indexOf(x))[0] ?? 'ok';
}

export function calMagTier(table: ThresholdTable | undefined, calcium: Num, magnesium: Num, ctx?: Context): Tier {
  return worstTier([
    evaluateTier(table, 'calcium_ppm', toNum(calcium), ctx),
    evaluateTier(table, 'magnesium_ppm', toNum(magnesium), ctx),
  ]);
}
